import { cn } from "@/lib/utils";
import { Check } from "lucide-react";

interface GameStepsProps {
  labels: string[];
  activeIndex: number;
  // Every step reached (the offer has been redeemed).
  isComplete: boolean;
  ariaLabel: string;
}

type StepStatus = "idle" | "on" | "done";

const circleClass: Record<StepStatus, string> = {
  idle: "border-2 border-line bg-white text-ink-muted",
  on: "bg-primary text-white ring-4 ring-primary/20",
  done: "bg-tone-mint-ink text-white",
};

const labelClass: Record<StepStatus, string> = {
  idle: "text-ink-muted",
  on: "text-ink",
  done: "text-tone-mint-ink",
};

// Progress stepper over a game demo (Spin -> Reveal offer -> Redeem): circles
// joined by a line, labels underneath. The current step is filled red with a
// soft ring, finished steps turn green with a tick (and so does the line
// leading out of them), upcoming ones stay outlined.
const GameSteps = ({
  labels,
  activeIndex,
  isComplete,
  ariaLabel,
}: GameStepsProps) => (
  <ol aria-label={ariaLabel} className="mx-auto flex w-full max-w-[560px]">
    {labels.map((label, index) => {
      const status: StepStatus =
        isComplete || index < activeIndex
          ? "done"
          : index === activeIndex
            ? "on"
            : "idle";
      const isLast = index === labels.length - 1;
      return (
        <li
          key={label}
          aria-current={status === "on" ? "step" : undefined}
          className="relative flex min-w-0 flex-1 flex-col items-center gap-2 text-center"
        >
          {/* Connector to the next step, drawn behind the circles. */}
          {!isLast && (
            <span
              aria-hidden="true"
              className={cn(
                "absolute top-[17px] left-1/2 h-[3px] w-full rounded-full transition-colors duration-300 motion-reduce:transition-none",
                status === "done" ? "bg-tone-mint-ink" : "bg-line",
              )}
            />
          )}
          <span
            aria-hidden="true"
            className={cn(
              "relative z-[1] grid size-9 place-items-center rounded-full text-sm font-extrabold transition-colors duration-300 motion-reduce:transition-none",
              circleClass[status],
            )}
          >
            {status === "done" ? (
              <Check className="size-[18px]" strokeWidth={3} />
            ) : (
              index + 1
            )}
          </span>
          <span
            className={cn(
              "px-1 text-[13px] leading-tight font-bold sm:text-sm",
              labelClass[status],
            )}
          >
            {label}
          </span>
        </li>
      );
    })}
  </ol>
);

export default GameSteps;
